interface ExtractHtmlOptions {
  includeStyles?: boolean
  sanitize?: boolean
  pageTitle?: string
  prependCustomStyles?: string | string[]
  appendCustomStyles?: string | string[]
  hideModals?: boolean
}

export const extractHtmlWithStyles = async (element: HTMLElement | null, options: ExtractHtmlOptions = {}): Promise<string> => {
  if (!element) {
    throw new Error('Element not found')
  }

  const { includeStyles = true, sanitize = true, pageTitle = 'Generated Document', prependCustomStyles, appendCustomStyles, hideModals } = options

  let styles = ''
  let prependCustomStylesContent = ''
  let appendCustomStylesContent = ''
  let modalHidingStyles = ''

  if (prependCustomStyles) {
    if (Array.isArray(prependCustomStyles)) {
      prependCustomStylesContent = prependCustomStyles.join('\n')
    } else {
      prependCustomStylesContent = prependCustomStyles
    }
  }

  if (appendCustomStyles) {
    if (Array.isArray(appendCustomStyles)) {
      appendCustomStylesContent = appendCustomStyles.join('\n')
    } else {
      appendCustomStylesContent = appendCustomStyles
    }
  }

  if (hideModals) {
    modalHidingStyles = `
        .euiOverlayMask {
            display: none !important;
        }
        .euiModal {
            display: none !important;
        }`
  }

  if (includeStyles) {
    const regex = /^html body {.*?min-width:/
    const cssRules: string[] = []

    const allStyleSheets = Array.from(document.styleSheets)
    allStyleSheets.forEach(styleSheet => {
      try {
        if (styleSheet.cssRules) {
          Array.from(styleSheet.cssRules).forEach(rule => {
            if (regex.test(rule.cssText)) {
              return
            }

            cssRules.push(rule.cssText)
          })
        }
      } catch (e) {
        console.warn('Could not access stylesheet:', e)
      }
    })

    const styleTags = document.querySelectorAll('style')
    styleTags.forEach(styleTag => {
      if (!styleTag.textContent || regex.test(styleTag.textContent)) {
        return
      }

      cssRules.push(styleTag.textContent)
    })

    const uniqueRules = [...new Set(cssRules.filter(rule => rule.trim().length > 0))]
    styles = uniqueRules.join('\n')
  }

  const clonedElement = element.cloneNode(true) as HTMLElement

  await expandRelativeUrls(clonedElement)

  if (sanitize) {
    sanitizeElement(clonedElement)
  }

  const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="stylesheet" href="https://use.typekit.net/bhl1axb.css">
    <title>${pageTitle}</title>
    <style>
        .do_not_include_in_html_generation {
            display: none !important;
        }
        ${modalHidingStyles}
        ${prependCustomStylesContent}
        ${styles}
        ${appendCustomStylesContent}
    </style>
</head>
<body>
    ${clonedElement.outerHTML}
</body>
</html>`

  return htmlContent
}

const expandRelativeUrls = async (element: HTMLElement): Promise<void> => {
  const attributesToExpand = ['src', 'href', 'action']
  const baseUrl = window.location.origin

  const convertImageToInline = async (el: Element, url: string): Promise<string> => {
    if (el.tagName.toLowerCase() !== 'img') {
      return url
    }

    try {
      const response = await fetch(url)
      const contentType = response.headers.get('content-type') || ''
      const isSvg = contentType.includes('svg') || url.toLowerCase().includes('.svg')

      if (isSvg) {
        const svgText = await response.text()
        const svgElement = document.createElement('div')
        svgElement.innerHTML = svgText
        const svg = svgElement.querySelector('svg')

        if (svg) {
          const imgAttributes = el.attributes
          for (let i = 0; i < imgAttributes.length; i++) {
            const attr = imgAttributes[i]
            if (attr.name !== 'src') {
              svg.setAttribute(attr.name, attr.value)
            }
          }

          el.parentNode?.replaceChild(svg, el)
          return ''
        }
      }

      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve(reader.result as string)
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch {
      return expandUrl(url)
    }
  }

  const expandUrl = (url: string): string => {
    if (!url || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('#')) {
      return url
    }

    if (url.startsWith('/')) {
      return `${baseUrl}${url}`
    }

    return url
  }

  for (const attr of attributesToExpand) {
    const elementsWithAttribute = element.querySelectorAll(`[${attr}]`)
    for (const el of elementsWithAttribute) {
      const currentValue = el.getAttribute(attr)
      if (currentValue) {
        let expandedUrl = expandUrl(currentValue)

        if (attr === 'src' && el.tagName.toLowerCase() === 'img') {
          expandedUrl = await convertImageToInline(el, expandedUrl)
        }

        if (expandedUrl) {
          el.setAttribute(attr, expandedUrl)
        }
      }
    }
  }

  if (element.hasAttribute('src')) {
    const currentSrc = element.getAttribute('src')
    if (currentSrc) {
      let expandedUrl = expandUrl(currentSrc)

      if (element.tagName.toLowerCase() === 'img') {
        expandedUrl = await convertImageToInline(element, expandedUrl)
      }

      if (expandedUrl) {
        element.setAttribute('src', expandedUrl)
      }
    }
  }

  if (element.hasAttribute('href')) {
    const currentHref = element.getAttribute('href')
    if (currentHref) {
      element.setAttribute('href', expandUrl(currentHref))
    }
  }
}

const sanitizeElement = (element: HTMLElement): void => {
  const dangerousAttributes = ['onclick', 'onload', 'onerror', 'onmouseover']
  const dangerousTags = ['script', 'iframe', 'object', 'embed']

  dangerousTags.forEach(tag => {
    const elements = element.querySelectorAll(tag)
    elements.forEach(el => el.remove())
  })

  const allElements = element.querySelectorAll('*')
  allElements.forEach(el => {
    dangerousAttributes.forEach(attr => {
      if (el.hasAttribute(attr)) {
        el.removeAttribute(attr)
      }
    })
  })

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null)

  const textNodes: Text[] = []
  let node: Node | null

  while ((node = walker.nextNode())) {
    textNodes.push(node as Text)
  }

  textNodes.forEach(textNode => {
    if (textNode.textContent) {
      textNode.textContent = textNode.textContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').replace(/javascript:/gi, '')
    }
  })
}

export const downloadBlobAsPdf = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  window.URL.revokeObjectURL(url)
}

export const downloadBlobAsImg = (blob: Blob, filename: string, format: 'jpeg' | 'png'): void => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith(`.${format}`) ? filename : `${filename}.${format}`

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  window.URL.revokeObjectURL(url)
}
